import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, errMsg, API } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Save, Palette, Building2, DollarSign, CreditCard, ExternalLink, AlertTriangle, Trash2, Bell, Download, Mail, Columns, Lock } from "lucide-react";
import { toast } from "sonner";
import { assetUrl } from "../lib/brand";

const CURRENCIES = [
  { code: "USD", label: "US Dollar — $" },
  { code: "EUR", label: "Euro — €" },
  { code: "GBP", label: "British Pound — £" },
  { code: "CAD", label: "Canadian Dollar — CA$" },
  { code: "AUD", label: "Australian Dollar — AU$" },
  { code: "INR", label: "Indian Rupee — ₹" },
  { code: "JPY", label: "Japanese Yen — ¥" },
  { code: "BRL", label: "Brazilian Real — R$" },
  { code: "CHF", label: "Swiss Franc — Fr" },
  { code: "SGD", label: "Singapore Dollar — S$" },
];

const PREF_LABELS = {
  slip_alerts: "Slippage alerts",
  weekly_digest: "Monday weekly digest",
  invites: "Teammate invite emails",
};

const PREF_HINTS = {
  slip_alerts: "Email + mobile push the moment calculated delivery slips past the stated due date.",
  weekly_digest: "Every Monday 9am UTC status pack to you (additional stakeholders you added keep receiving it).",
  invites: "Emails you receive when a colleague invites you to join their enterprise org.",
};

export default function SettingsPage() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();
  const [currency, setCurrency] = useState(user?.currency || "USD");
  const [org, setOrg] = useState(null);
  const [brand, setBrand] = useState({ name: "", logo_url: "", primary_color: "#1D4ED8", subtitle: "", division: "" });
  const [logoBusy, setLogoBusy] = useState(false);
  const [savingPref, setSavingPref] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(null); // "json" | "csv" | "xlsx" | null
  const [emailingExport, setEmailingExport] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [availableCols, setAvailableCols] = useState({});     // {tasks: [...], projects: [...]}
  const [pickedCols, setPickedCols] = useState({});           // {tasks: Set<string>}
  const [pickerFmt, setPickerFmt] = useState("csv");         // which format to download after pick
  const [prefs, setPrefs] = useState({ slip_alerts: true, weekly_digest: true, invites: true });
  const [prefsSaving, setPrefsSaving] = useState(null); // key currently saving
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpBusy, setCpBusy] = useState(false);
  const pushRegistered = useRef(false);

  const enterprise = user?.account_type === "enterprise" && !!user?.org_id;
  const subscribed = ["active", "trialing"].includes(user?.subscription_status);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { data: { return_url: window.location.href } });
      window.location.href = data.portal_url;
    } catch (e) { toast.error(errMsg(e)); setPortalBusy(false); }
  };

  useEffect(() => {
    if (!enterprise) return;
    api.get("/organizations/me").then(({ data }) => {
      setOrg(data);
      setBrand({
        name: data.name || "",
        logo_url: data.logo_url || "",
        primary_color: data.primary_color || "#1D4ED8",
        subtitle: data.subtitle || "",
        division: data.division || "",
      });
    }).catch(() => {});
  }, [enterprise]);

  // Mobile shell bridge — Compass PM Expo app injects window.EXPO_PUSH_TOKEN after
  // the user grants notification permission. Forward it to the backend exactly once.
  useEffect(() => {
    if (pushRegistered.current || !user?.user_id) return;
    const t = window.EXPO_PUSH_TOKEN;
    const platform = window.EXPO_PUSH_PLATFORM;
    if (!t) return;
    pushRegistered.current = true;
    api.post("/account/push-token", { token: t, platform })
      .then(() => console.info("Mobile push token registered"))
      .catch((e) => console.warn("push-token register failed", errMsg(e)));
  }, [user?.user_id]);

  // Hydrate notification prefs from the current user record
  useEffect(() => {
    if (user?.notification_prefs) {
      setPrefs({
        slip_alerts: user.notification_prefs.slip_alerts !== false,
        weekly_digest: user.notification_prefs.weekly_digest !== false,
        invites: user.notification_prefs.invites !== false,
      });
    }
  }, [user?.notification_prefs]);

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setPrefsSaving(key);
    try {
      await api.put("/account/notification-prefs", { [key]: next[key] });
      toast.success(`${PREF_LABELS[key]} ${next[key] ? "on" : "muted"}`);
      await refresh();
    } catch (e) {
      setPrefs(prefs); // revert
      toast.error(errMsg(e));
    }
    setPrefsSaving(null);
  };

  const exportData = async (fmt = "json") => {
    setExporting(fmt);
    try {
      const path = fmt === "csv" ? "/account/export.csv" : fmt === "xlsx" ? "/account/export.xlsx" : "/account/export";
      const res = await fetch(`${API}${path}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const ext = fmt === "csv" ? "zip" : fmt === "xlsx" ? "xlsx" : "json";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compass-pm-export-${(user?.email || "account").replace(/[^a-z0-9.@-]/gi, "_")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started — check your downloads folder");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
    setExporting(null);
  };

  const emailExport = async () => {
    setEmailingExport(true);
    try {
      const { data } = await api.post("/account/export/email", {});
      const expires = data?.expires_at ? new Date(data.expires_at).toLocaleDateString() : "7 days";
      toast.success(`Secure download link emailed to ${user?.email} — expires ${expires}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
    setEmailingExport(false);
  };

  const openPicker = async (fmt) => {
    setPickerFmt(fmt);
    setPickerOpen(true);
    if (Object.keys(availableCols).length === 0) {
      try {
        const { data } = await api.get("/account/export/columns");
        const cols = data?.collections || {};
        setAvailableCols(cols);
        // Default: pre-select everything so untouched = full export
        const defaults = {};
        Object.entries(cols).forEach(([k, v]) => { defaults[k] = new Set(v); });
        setPickedCols(defaults);
      } catch (e) {
        toast.error(errMsg(e));
        setPickerOpen(false);
      }
    }
  };

  const togglePickedCol = (coll, col) => {
    setPickedCols((prev) => {
      const next = { ...prev, [coll]: new Set(prev[coll] || []) };
      if (next[coll].has(col)) next[coll].delete(col); else next[coll].add(col);
      return next;
    });
  };

  const selectAllCols = (coll, select) => {
    setPickedCols((prev) => ({
      ...prev,
      [coll]: select ? new Set(availableCols[coll] || []) : new Set(),
    }));
  };

  const serializeFields = () => {
    // Only emit scoping for collections that are a strict subset of their available fields
    const segments = [];
    Object.entries(availableCols).forEach(([coll, allCols]) => {
      const picked = pickedCols[coll];
      if (!picked) return;
      if (picked.size > 0 && picked.size < allCols.length) {
        const ordered = allCols.filter((c) => picked.has(c));
        if (ordered.length) segments.push(`${coll}:${ordered.join(",")}`);
      } else if (picked.size === 0) {
        // user de-selected everything — emit a sentinel non-existent column so the collection renders empty
        segments.push(`${coll}:__none__`);
      }
    });
    return segments.join(";");
  };

  const downloadPicked = async () => {
    const fieldsStr = serializeFields();
    setExporting(pickerFmt);
    setPickerOpen(false);
    try {
      const path = pickerFmt === "xlsx" ? "/account/export.xlsx" : "/account/export.csv";
      const qs = fieldsStr ? `?fields=${encodeURIComponent(fieldsStr)}` : "";
      const res = await fetch(`${API}${path}${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const ext = pickerFmt === "xlsx" ? "xlsx" : "zip";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `compass-pm-export-${(user?.email || "account").replace(/[^a-z0-9.@-]/gi, "_")}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Download started with your picked columns");
    } catch (e) {
      toast.error(e.message || "Export failed");
    }
    setExporting(null);
  };

  const confirmMatches = (confirmEmail || "").trim().toLowerCase() === (user?.email || "").toLowerCase();
  const deleteAccount = async () => {
    if (!confirmMatches) { toast.error("Email does not match your account"); return; }
    if (!window.confirm("Permanently delete your Compass PM account and every project, task, risk, and invite you own? This cannot be undone.")) return;
    setDeleting(true);
    try {
      await api.delete("/account", { data: { confirm_email: confirmEmail } });
      toast.success("Your account has been deleted.");
      await logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setDeleting(false);
    }
  };

  const savePref = async (e) => {
    e.preventDefault();
    setSavingPref(true);
    try {
      await api.put("/account/preferences", { currency });
      window.__wpm_currency = currency;
      await refresh();
      toast.success(`Currency set to ${currency}`);
    } catch (e2) { toast.error(errMsg(e2)); }
    setSavingPref(false);
  };

  const saveBrand = async (e) => {
    e.preventDefault();
    setSavingBrand(true);
    try {
      const { data } = await api.put("/organizations/me", brand);
      setOrg(data);
      await refresh();
      toast.success("Branding saved — client portal updated");
    } catch (e2) { toast.error(errMsg(e2)); }
    setSavingBrand(false);
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/svg+xml"].includes(f.type)) { toast.error("Logo must be a PNG, JPG or SVG image"); return; }
    if (f.size > 2 * 1024 * 1024) { toast.error("Logo must be 2 MB or smaller"); return; }
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/organizations/me/logo", fd);
      setOrg(data);
      setBrand((b) => ({ ...b, logo_url: data.logo_url || "" }));
      await refresh();
      toast.success("Logo uploaded");
    } catch (e2) { toast.error(errMsg(e2)); }
    setLogoBusy(false);
  };

  return (
    <AppLayout>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white" style={{fontFamily:'Outfit'}}>Account & brand</h1>
        <p className="text-slate-400 mt-1">Personal preferences and — for enterprise orgs — the white-labelled client portal.</p>
      </div>

      <form onSubmit={savePref} className="pc-card p-6 mb-6" data-testid="preferences-form">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-1">
          <DollarSign size={12}/> Preferences
        </div>
        <div className="text-lg font-semibold text-white mb-4" style={{fontFamily:'Outfit'}}>Base currency</div>
        <div className="grid sm:grid-cols-2 gap-4 max-w-2xl">
          <div>
            <label className="text-xs text-slate-400">Currency</label>
            <select data-testid="currency-select" value={currency} onChange={(e)=>setCurrency(e.target.value)}
              className="w-full mt-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm">
              {CURRENCIES.map((c)=> <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <div className="text-xs text-slate-500 mt-1">Applied to every Est/Actual/Variance figure across the app, PDFs, and emails.</div>
          </div>
          <div className="flex items-end">
            <button type="submit" disabled={savingPref} data-testid="save-preferences-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-60">
              <Save size={14}/> {savingPref ? "Saving…" : "Save preference"}
            </button>
          </div>
        </div>
      </form>

      <div className="pc-card p-6 mb-6" data-testid="billing-section">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-1">
          <CreditCard size={12}/> Billing
        </div>
        <div className="text-lg font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Subscription & seats</div>
        <div className="text-sm text-slate-400 mb-5">
          Current status: <span className="text-slate-200 font-medium capitalize" data-testid="subscription-status">{user?.subscription_status || "inactive"}</span>
          {" · "}Open the Stripe billing portal to change seats, update your card, download invoices, or cancel.
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={openPortal} disabled={portalBusy || !subscribed} data-testid="open-billing-portal-btn"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-60">
            <ExternalLink size={14}/> {portalBusy ? "Opening…" : "Manage billing"}
          </button>
          {!subscribed && (
            <a href="/pricing" data-testid="go-to-pricing-link" className="text-sm text-slate-400 hover:text-white">Start a plan →</a>
          )}
        </div>
      </div>

      {enterprise ? (
        <form onSubmit={saveBrand} className="pc-card p-6" data-testid="brand-form">
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-1">
            <Palette size={12}/> Branded client portal
          </div>
          <div className="text-lg font-semibold text-white mb-4" style={{fontFamily:'Outfit'}}>White-label your public share pages</div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-xs text-slate-400">Organization name <span className="text-slate-600">(shown on portal)</span></label>
              <input value={brand.name} onChange={(e)=>setBrand({...brand, name:e.target.value})} data-testid="brand-name"
                className="w-full mt-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
            </div>
            <div>
              <label className="text-xs text-slate-400">Subtitle / tagline <span className="text-slate-600">(optional)</span></label>
              <input value={brand.subtitle} onChange={(e)=>setBrand({...brand, subtitle:e.target.value})} data-testid="brand-subtitle"
                className="w-full mt-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
            </div>
            <div>
              <label className="text-xs text-slate-400">Division <span className="text-slate-600">(optional)</span></label>
              <input value={brand.division} onChange={(e)=>setBrand({...brand, division:e.target.value})} data-testid="brand-division"
                className="w-full mt-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
            </div>
            <div className="sm:col-span-2">
              <label className="text-xs text-slate-400">Logo <span className="text-slate-600">(upload PNG, JPG or SVG up to 2 MB, or paste an https URL)</span></label>
              <div className="flex flex-wrap items-center gap-3 mt-1">
                <label className={`inline-flex items-center gap-2 border border-slate-700 hover:border-blue-500 text-slate-200 px-3 py-2 rounded-lg text-sm cursor-pointer ${logoBusy ? "opacity-60 pointer-events-none" : ""}`} data-testid="brand-logo-upload-btn">
                  {logoBusy ? "Uploading…" : "Upload logo"}
                  <input type="file" accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" className="hidden" onChange={uploadLogo} data-testid="brand-logo-file" />
                </label>
                <input value={brand.logo_url} onChange={(e)=>setBrand({...brand, logo_url:e.target.value})} data-testid="brand-logo-url" placeholder="https://cdn.yourbrand.com/logo.svg"
                  className="flex-1 min-w-[200px] px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm"/>
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-400">Accent colour</label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={brand.primary_color} onChange={(e)=>setBrand({...brand, primary_color:e.target.value})} data-testid="brand-color"
                  className="w-10 h-10 rounded cursor-pointer border border-slate-700 bg-slate-900"/>
                <input value={brand.primary_color} onChange={(e)=>setBrand({...brand, primary_color:e.target.value})}
                  className="flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm font-mono"/>
              </div>
            </div>
          </div>

          {/* Preview */}
          <div className="mt-6 rounded-lg border border-slate-800 overflow-hidden" data-testid="brand-preview">
            <div className="p-4 flex items-center gap-3" style={{background: `linear-gradient(90deg, ${brand.primary_color} 0%, ${brand.primary_color}80 100%)`}}>
              {brand.logo_url ? (
                <img src={assetUrl(brand.logo_url)} alt="logo" className="h-8 w-auto bg-white/10 rounded" data-testid="brand-preview-logo"/>
              ) : (
                <Building2 className="text-white" size={22}/>
              )}
              <div>
                <div className="text-white font-bold" style={{fontFamily:'Outfit'}}>{brand.name || "Your organization"}{brand.division ? ` · ${brand.division}` : ""}</div>
                {brand.subtitle && <div className="text-xs text-white/80">{brand.subtitle}</div>}
              </div>
              <div className="ml-auto text-[11px] text-white/80 uppercase tracking-widest">Live preview</div>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-end">
            <button type="submit" disabled={savingBrand} data-testid="save-brand-btn"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-60">
              <Save size={14}/> {savingBrand ? "Saving…" : "Save branding"}
            </button>
          </div>
        </form>
      ) : (
        <div className="pc-card p-6 text-slate-400 text-sm" data-testid="brand-locked">
          Branded client portal is an enterprise feature. Create an enterprise account to white-label your public share pages.
        </div>
      )}

      {/* NOTIFICATION PREFERENCES — per-type mute controls */}
      <div className="pc-card p-6 mt-6" data-testid="notification-prefs">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-1">
          <Bell size={12}/> Notifications
        </div>
        <div className="text-lg font-semibold text-white mb-4" style={{fontFamily:'Outfit'}}>What you want to hear about</div>
        <div className="divide-y divide-slate-800/70">
          {["slip_alerts", "weekly_digest", "invites"].map((k) => (
            <div key={k} className="py-4 flex items-start sm:items-center justify-between gap-6 flex-wrap sm:flex-nowrap">
              <div className="min-w-0">
                <div className="text-sm font-medium text-white">{PREF_LABELS[k]}</div>
                <div className="text-xs text-slate-400 mt-0.5 max-w-md">{PREF_HINTS[k]}</div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={prefs[k]}
                onClick={() => togglePref(k)}
                disabled={prefsSaving === k}
                data-testid={`pref-toggle-${k}`}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  prefs[k] ? "bg-blue-500" : "bg-slate-700"
                } disabled:opacity-50`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    prefs[k] ? "translate-x-5" : "translate-x-0.5"
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* ACCOUNT EXPORT + DANGER ZONE — Apple 5.1.1 + GDPR portability */}
      <div className="mt-6 pc-card p-6" data-testid="data-export">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-300/80 mb-1">
          <Download size={12}/> Your data
        </div>
        <div className="text-lg font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Download everything</div>
        <p className="text-sm text-slate-400 mb-4 max-w-2xl">
          Get a single JSON file with your user profile, organization, projects, tasks, risks, templates, invites,
          and audit log — the complete record we store about you. Use it for GDPR portability requests or just to keep an off-site backup.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => exportData("json")}
            disabled={!!exporting}
            data-testid="export-data-btn"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60"
          >
            <Download size={14}/> {exporting === "json" ? "Preparing…" : "JSON"}
          </button>
          <button
            onClick={() => exportData("csv")}
            disabled={!!exporting}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60"
          >
            <Download size={14}/> {exporting === "csv" ? "Preparing…" : "CSV (zip)"}
          </button>
          <button
            onClick={() => exportData("xlsx")}
            disabled={!!exporting}
            data-testid="export-xlsx-btn"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60"
          >
            <Download size={14}/> {exporting === "xlsx" ? "Preparing…" : "Excel (xlsx)"}
          </button>
          <button
            onClick={() => openPicker("csv")}
            disabled={!!exporting}
            data-testid="export-pick-cols-btn"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60"
          >
            <Columns size={14}/> Pick columns…
          </button>
          <button
            onClick={emailExport}
            disabled={emailingExport}
            data-testid="email-export-btn"
            className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-white px-4 py-2 rounded-lg text-sm disabled:opacity-60"
          >
            <Mail size={14}/> {emailingExport ? "Sending…" : "Email it to me"}
          </button>
        </div>
        <div className="text-xs text-slate-500 mt-3">
          CSV and Excel open directly in Numbers / Microsoft Excel — one sheet (or file) per collection, nested fields JSON-encoded.
          "Email it to me" sends a secure, 7-day download link to <span className="text-slate-300">{user?.email}</span> so you can retrieve the archive from any browser.
        </div>
      </div>

      {/* DANGER ZONE — Apple 5.1.1 compliant in-app account deletion */}
      <div className="mt-6 rounded-xl border border-slate-800 bg-slate-900/40 p-6" data-testid="change-password-panel">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400 mb-1">
          <Lock size={12}/> Security
        </div>
        <div className="text-lg font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Change password</div>
        <p className="text-sm text-slate-400 mb-4 max-w-2xl">
          Rotate your password without the reset-email round trip. Minimum 8 characters.
        </p>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (cpNew.length < 8) { toast.error("New password must be at least 8 characters."); return; }
            if (cpNew !== cpConfirm) { toast.error("New password and confirmation don't match."); return; }
            if (cpNew === cpCurrent) { toast.error("New password must be different from current password."); return; }
            setCpBusy(true);
            try {
              await api.post("/auth/change-password", { current_password: cpCurrent, new_password: cpNew });
              toast.success("Password updated");
              setCpCurrent(""); setCpNew(""); setCpConfirm("");
            } catch (e2) { toast.error(errMsg(e2)); }
            setCpBusy(false);
          }}
          className="grid gap-3 sm:grid-cols-3 max-w-3xl"
        >
          <input
            type="password" required value={cpCurrent}
            onChange={(e)=>setCpCurrent(e.target.value)}
            placeholder="Current password"
            data-testid="change-password-current"
            className="px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:border-blue-500"
          />
          <input
            type="password" required value={cpNew}
            onChange={(e)=>setCpNew(e.target.value)}
            placeholder="New password (8+ chars)"
            data-testid="change-password-new"
            className="px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:border-blue-500"
          />
          <input
            type="password" required value={cpConfirm}
            onChange={(e)=>setCpConfirm(e.target.value)}
            placeholder="Confirm new password"
            data-testid="change-password-confirm"
            className="px-3 py-2.5 rounded-lg border border-slate-700 bg-slate-950 text-white text-sm focus:outline-none focus:border-blue-500"
          />
          <button
            type="submit"
            disabled={cpBusy || !cpCurrent || !cpNew || !cpConfirm}
            data-testid="change-password-submit"
            className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-500 to-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow-lg shadow-blue-900/30 hover:from-blue-400 hover:to-blue-600 disabled:opacity-60 w-fit"
          >
            <Lock size={14}/> {cpBusy ? "Updating…" : "Update password"}
          </button>
        </form>
      </div>

      {/* DANGER ZONE — Apple 5.1.1 compliant in-app account deletion */}
      <div className="mt-6 rounded-xl border border-red-500/30 bg-red-950/20 p-6" data-testid="danger-zone">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-red-300 mb-1">
          <AlertTriangle size={12}/> Danger zone
        </div>
        <div className="text-lg font-semibold text-white mb-1" style={{fontFamily:'Outfit'}}>Delete my account</div>
        <p className="text-sm text-slate-300 mb-4 max-w-2xl">
          This permanently removes your account and every project, task, risk, invite, teammate, template, and
          audit entry you own. Any active Stripe subscription is cancelled. This cannot be undone.
          Confirm by retyping your account email: <span className="text-slate-100 font-mono">{user?.email}</span>
        </p>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="email"
            value={confirmEmail}
            onChange={(e) => setConfirmEmail(e.target.value)}
            placeholder="Retype your email to confirm"
            data-testid="delete-confirm-email"
            className="flex-1 min-w-[260px] max-w-md px-3 py-2 rounded-lg border border-red-500/40 bg-slate-950 text-white placeholder-slate-500 focus:outline-none focus:border-red-400 text-sm"
          />
          <button
            onClick={deleteAccount}
            disabled={!confirmMatches || deleting}
            data-testid="delete-account-btn"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 disabled:bg-red-900/60 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            <Trash2 size={14}/> {deleting ? "Deleting…" : "Delete my account"}
          </button>
        </div>
      </div>

      {/* COLUMN PICKER MODAL */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          onClick={() => setPickerOpen(false)}
          data-testid="column-picker-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col rounded-xl border border-slate-700 bg-slate-950"
          >
            <div className="p-5 border-b border-slate-800 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-blue-300/80 inline-flex items-center gap-2 mb-1"><Columns size={12}/> Pick columns</div>
                <div className="text-lg font-semibold text-white" style={{fontFamily:'Outfit'}}>Choose fields for each sheet</div>
                <div className="text-xs text-slate-400 mt-1">Narrow the export to just the columns you want. Un-picked collections stay in the file with all their columns.</div>
              </div>
              <button
                onClick={() => setPickerOpen(false)}
                className="text-slate-400 hover:text-white text-sm"
                data-testid="column-picker-close"
              >Close</button>
            </div>
            <div className="p-5 overflow-y-auto flex-1 space-y-5">
              {Object.keys(availableCols).length === 0 ? (
                <div className="text-slate-400 text-sm">Loading your fields…</div>
              ) : (
                Object.entries(availableCols).map(([coll, cols]) => {
                  const picked = pickedCols[coll] || new Set();
                  return (
                    <div key={coll} className="border border-slate-800 rounded-lg p-4" data-testid={`col-group-${coll}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-semibold text-white capitalize">{coll} <span className="text-xs text-slate-500 font-normal">· {picked.size}/{cols.length} picked</span></div>
                        <div className="flex gap-2 text-xs">
                          <button onClick={() => selectAllCols(coll, true)} className="text-blue-400 hover:text-blue-300" data-testid={`col-all-${coll}`}>All</button>
                          <span className="text-slate-700">|</span>
                          <button onClick={() => selectAllCols(coll, false)} className="text-slate-400 hover:text-white" data-testid={`col-none-${coll}`}>None</button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                        {cols.map((c) => (
                          <label key={c} className="inline-flex items-center gap-2 text-xs text-slate-200 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={picked.has(c)}
                              onChange={() => togglePickedCol(coll, c)}
                              className="accent-blue-500 h-3.5 w-3.5"
                              data-testid={`col-${coll}-${c}`}
                            />
                            <span className="font-mono">{c}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
            <div className="p-5 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 bg-slate-900/40">
              <div className="flex items-center gap-2 text-xs text-slate-400">
                Format:
                <button
                  onClick={() => setPickerFmt("csv")}
                  className={`px-2 py-1 rounded ${pickerFmt === "csv" ? "bg-blue-500 text-white" : "bg-slate-800 text-slate-300"}`}
                  data-testid="picker-fmt-csv"
                >CSV (zip)</button>
                <button
                  onClick={() => setPickerFmt("xlsx")}
                  className={`px-2 py-1 rounded ${pickerFmt === "xlsx" ? "bg-blue-500 text-white" : "bg-slate-800 text-slate-300"}`}
                  data-testid="picker-fmt-xlsx"
                >Excel</button>
              </div>
              <button
                onClick={downloadPicked}
                disabled={!!exporting}
                data-testid="picker-download-btn"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-60"
              >
                <Download size={14}/> Download with picked columns
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
